import {Link, useNavigate} from 'react-router';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);
import {ScenarioAttemptMinimal} from '@types';
import {UserItem, ScenarioResultBadge} from '@components';
import {useTheme} from '@components/theme/useTheme';
import {Loading} from '@components/Loading';
import {useAuthenticated} from '@utils/hooks/common';
import {getScenarioTexts} from '@utils';

type Props = {
    attempts: ScenarioAttemptMinimal[];
    showProblemColumn?: boolean;
    showUserColumn?: boolean;
    emptyMessage?: string;
    isLoading?: boolean;
};

export const InvasionAttemptsTable = ({
    attempts,
    showProblemColumn = false,
    showUserColumn = true,
    emptyMessage,
    isLoading = false,
}: Props) => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const theme = useTheme();
    const {isAdmin} = useAuthenticated();
    const isDark = theme === 'dark';

    if (isLoading || attempts.length === 0) {
        return (
            <div className="invasion-attempts-state text-center py-8 text-body-secondary">
                {isLoading ? (
                    <Loading />
                ) : (
                    (emptyMessage ?? t('invasions.no_attempts'))
                )}
            </div>
        );
    }

    const thClass =
        'pb-2 md:pb-3 text-body-secondary font-semibold text-xs md:text-base';
    const tdClass = 'p-2 md:py-2 md:px-0';
    const borderClass = isDark ? 'border-neutral-700' : 'border-neutral-200';

    return (
        <div className="invasion-attempts-scroll overflow-x-auto">
            <table
                className={`invasion-attempts-table w-full text-left text-sm md:text-base${showProblemColumn ? ' with-problem' : ''}`}>
                <thead>
                    <tr className={`border-b-2 ${borderClass}`}>
                        <th className={thClass}>{t('invasions.date')}</th>
                        {showUserColumn && (
                            <th
                                className={`${thClass} min-w-[120px] md:min-w-[150px]`}>
                                {t('invasions.user')}
                            </th>
                        )}
                        {showProblemColumn && (
                            <th className={thClass}>
                                {t('common.problem')}
                            </th>
                        )}
                        <th className={thClass}>{t('invasions.difficulty')}</th>
                        <th className={thClass}>{t('invasions.move_count')}</th>
                        <th className={thClass}>{t('invasions.score')}</th>
                        <th className={thClass}>{t('invasions.result')}</th>
                    </tr>
                </thead>
                <tbody className="text-body-secondary">
                    {attempts.map(attempt => {
                        const moveCount = attempt.moveCount;
                        const score = attempt.score;
                        const scoreColor =
                            score >= 0 ? 'text-green-600' : 'text-red-600';

                        const formatScore = () => {
                            if (moveCount === 0) return '-';
                            const absScore = Math.abs(score);
                            const formatted =
                                absScore < 2
                                    ? score.toFixed(1)
                                    : Math.round(score).toString();
                            return score > 0 ? `+${formatted}` : formatted;
                        };

                        return (
                            <tr
                                key={attempt.id}
                                onClick={() =>
                                    isAdmin &&
                                    attempt.user &&
                                    navigate(
                                        `/invasions/${attempt.scenario.id}/attempts/${attempt.id}`
                                    )
                                }
                                className={`border-b ${
                                    isAdmin && attempt.user
                                        ? 'hover:bg-body-secondary cursor-pointer'
                                        : ''
                                } transition-colors ${borderClass}`}>
                                <td
                                    className={tdClass}
                                    title={dayjs(attempt.startedAt).format(
                                        'YYYY-MM-DD HH:mm'
                                    )}>
                                    {dayjs(attempt.startedAt).fromNow()}
                                </td>
                                {showUserColumn && (
                                    <td className={tdClass}>
                                        <div
                                            className="inline-block"
                                            onClick={e => e.stopPropagation()}>
                                            <UserItem
                                                user={attempt.user}
                                                anonymous={!attempt.user}
                                                avatarClassName="!ml-0 !w-5 !h-5 md:!w-6 md:!h-6"
                                                textClassName="text-xs md:text-sm"
                                            />
                                        </div>
                                    </td>
                                )}
                                {showProblemColumn && (
                                    <td
                                        className={tdClass}
                                        onClick={e => e.stopPropagation()}>
                                        <Link
                                            to={`/invasions/${attempt.scenario.id}`}
                                            className="hover:underline">
                                            {`${t(getScenarioTexts(attempt.scenario.type).problemLabelKey)} #${attempt.scenario.id}`}
                                        </Link>
                                    </td>
                                )}
                                <td className={tdClass}>
                                    {attempt.difficulty}
                                </td>
                                <td className={tdClass}>{moveCount || '-'}</td>
                                <td
                                    className={`${tdClass} font-semibold ${
                                        moveCount > 0 ? scoreColor : ''
                                    }`}>
                                    {formatScore()}
                                </td>
                                <td className={tdClass}>
                                    <ScenarioResultBadge
                                        result={attempt.result}
                                        didResign={attempt.didResign}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
